/**
 * BEVISVALVET — de två Ed25519-signerade bevisen och enhetens id.
 *
 * KONTRAKTET (shared/types.ts avsnitt 9). Klienten bär TVÅ sorters bevis,
 * giltiga i LICENSE_TTL_DAYS = 30 dagar:
 *   AccountProof   ett stycke, för auth 'account' (ME, UPDATE_ACCOUNT,
 *                  CREATE_COMPANY)
 *   LicenseProof   ETT PER FIRMA, för auth 'company' — beviset måste gälla
 *                  firman i SÖKVÄGEN. Ett giltigt bevis för firma A mot
 *                  /api/companies/B/... är FORBIDDEN (worker/src/auth/
 *                  authenticate.ts), inte UNAUTHENTICATED.
 *
 * BEVISET FÖRNYAS TYST (beslut 12). Varje SYNC_PUSH- och SYNC_PULL-svar bär en
 * färsk LicenseProof, och ME/AUTH_VERIFY bär hela uppsättningen. api/klient.ts
 * matar in dem här AUTOMATISKT efter varje lyckat svar — ingen skärm ska
 * behöva komma ihåg det, för den skärm som glömmer det loggar ut sin användare
 * efter 30 dagar utan att någon förstår varför.
 *
 * ROLLEN I BEVISET AVGÖR INGENTING. LicenseClaims bär role och
 * subscription_status, men servern läser BÅDA ur D1 vid varje anrop; bevisets
 * kopior är klientens OFFLINEKOPIA (beslut 11 mot 12). Klienten läser därför
 * rollen ur CompanySummary i ME-svaret, inte ur beviset — se app/session.tsx.
 *
 * VARFÖR localStorage. Kontraktet kräver Authorization: Bearer, alltså måste
 * klienten kunna LÄSA sin token; en httpOnly-kaka går inte att läsa och skulle
 * dessutom inte överleva den offlineanvändning beslut 12 finns för. Priset är
 * att en XSS på vår egen origin kommer åt beviset — samma pris som varje
 * bearer-baserad PWA betalar. Steg 5 kan flytta valvet till IndexedDB
 * tillsammans med utkorgen utan att en enda anropsplats ändras: hela lagringen
 * ligger bakom `las`, `spara` och `rensa` nedan.
 *
 * localStorage KAN KASTA. Privat läge, blockerade webbplatsdata och
 * miniatyrrendering ger både tomma läsningar och riktiga undantag. Varje
 * beröring ligger därför i try/catch, och ett valv som inte går att läsa är
 * ett UTLOGGAT valv — aldrig en krasch.
 */

import type { AccountProof, CompanyId, DeviceId, LicenseProof, MeResponse, Millis } from '@delat/types';
import { nyUlid } from './id';

const NYCKEL = 'klartid.bevis.v1';
const ENHETSNYCKEL = 'klartid.enhet.v1';

export interface Bevisstat {
  readonly konto: AccountProof | null;
  readonly licenser: readonly LicenseProof[];
}

const TOMT: Bevisstat = { konto: null, licenser: [] };

/* --- localStorage, alltid genom de här två ------------------------------ */

const lasRatt = (nyckel: string): string | null => {
  try {
    return globalThis.localStorage?.getItem(nyckel) ?? null;
  } catch {
    return null;
  }
};

const skrivRatt = (nyckel: string, varde: string | null): void => {
  try {
    if (varde === null) globalThis.localStorage?.removeItem(nyckel);
    else globalThis.localStorage?.setItem(nyckel, varde);
  } catch {
    /* Ett valv som inte går att skriva är ett valv som glömmer vid omladdning.
       Appen fungerar ändå under sessionen; att krascha vore sämre. */
  }
};

/* --- Enhetens id -------------------------------------------------------- */

/**
 * device_id följer med i AUTH_VERIFY och i varje SYNC_PUSH-kuvert, och det är
 * halva idempotensnyckeln: sync_op_log slår upp på (op_id, account_id,
 * device_id), så ett op_id som tillhör en ANNAN enhet ger FORBIDDEN i stället
 * för ett uppspelat svar (shared/errors.ts, DUPLICATE_OP). Id:t måste därför
 * vara STABILT över sessioner på samma telefon och skilja sig mellan två.
 */
export const enhetsId = (): DeviceId => {
  const sparat = lasRatt(ENHETSNYCKEL);
  if (sparat !== null && sparat.length >= 20 && sparat.length <= 40) {
    return sparat as DeviceId;
  }
  const nytt = nyUlid();
  skrivRatt(ENHETSNYCKEL, nytt);
  return nytt as DeviceId;
};

/* --- Valvet ------------------------------------------------------------- */

const tolka = (rad: string | null): Bevisstat => {
  if (rad === null) return TOMT;
  try {
    const tolkat: unknown = JSON.parse(rad);
    if (typeof tolkat !== 'object' || tolkat === null) return TOMT;
    const o = tolkat as Record<string, unknown>;
    const konto = o['konto'];
    const licenser = o['licenser'];
    return {
      konto: arAccountProof(konto) ? konto : null,
      licenser: Array.isArray(licenser) ? licenser.filter(arLicenseProof) : [],
    };
  } catch {
    return TOMT;
  }
};

const arAccountProof = (v: unknown): v is AccountProof =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as AccountProof).token === 'string' &&
  typeof (v as AccountProof).expires_at === 'number';

const arLicenseProof = (v: unknown): v is LicenseProof =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as LicenseProof).token === 'string' &&
  typeof (v as LicenseProof).company_id === 'string' &&
  typeof (v as LicenseProof).expires_at === 'number';

/**
 * Bevisvalvet. EN instans per app (se `valvet` nederst). Den är en
 * extern lagring i React-mening: `prenumerera` + `las` matar
 * useSyncExternalStore, så att varje komponent som visar inloggningsläget
 * ritas om när beviset byts eller rensas.
 */
export class Bevisvalv {
  #stat: Bevisstat;
  readonly #lyssnare = new Set<() => void>();

  constructor() {
    this.#stat = tolka(lasRatt(NYCKEL));
  }

  /** Ögonblicksbilden. SAMMA objekt så länge ingenting ändrats. */
  las = (): Bevisstat => this.#stat;

  prenumerera = (lyssnare: () => void): (() => void) => {
    this.#lyssnare.add(lyssnare);
    return () => {
      this.#lyssnare.delete(lyssnare);
    };
  };

  #satt(nytt: Bevisstat): void {
    this.#stat = nytt;
    skrivRatt(NYCKEL, JSON.stringify(nytt));
    for (const lyssnare of this.#lyssnare) lyssnare();
  }

  /** Hela uppsättningen ur ett ME- eller AUTH_VERIFY-svar. */
  sparaFranMe = (svar: MeResponse): void => {
    this.#satt({ konto: svar.account_proof, licenser: [...svar.licenses] });
  };

  /**
   * EN färsk licens ur ett push-/pull-svar eller ur CREATE_COMPANY. Ersätter
   * firmans gamla och lämnar de andra orörda.
   */
  fornyaLicens = (licens: LicenseProof): void => {
    const kvar = this.#stat.licenser.filter((l) => l.company_id !== licens.company_id);
    this.#satt({ konto: this.#stat.konto, licenser: [...kvar, licens] });
  };

  /** Bara kontobeviset, t.ex. efter UPDATE_ACCOUNT. */
  fornyaKonto = (konto: AccountProof): void => {
    this.#satt({ konto, licenser: this.#stat.licenser });
  };

  licensFor = (firma: CompanyId): LicenseProof | null =>
    this.#stat.licenser.find((l) => l.company_id === firma) ?? null;

  /**
   * EN firmas licens bort, de andra och kontobeviset orörda. Vägen ur en firma
   * som inte finns längre (DELETE_COMPANY): ME-svaret bär inte den raderade
   * firmans licens, men absorberingen i api/klient.ts LÄGGER BARA TILL och tar
   * aldrig bort — utan den här raden hade beviset legat kvar i telefonen i
   * upp till 30 dagar för en firma servern svarar FORBIDDEN om.
   */
  glomLicens = (firma: CompanyId): void => {
    const kvar = this.#stat.licenser.filter((l) => l.company_id !== firma);
    if (kvar.length === this.#stat.licenser.length) return;
    this.#satt({ konto: this.#stat.konto, licenser: kvar });
  };

  /** Utloggning. Enhetens id lämnas KVAR — det är telefonens, inte kontots. */
  rensa = (): void => {
    this.#satt(TOMT);
  };
}

/** Giltigt = inte utgått. Samma prov som workern gör (assertNotExpired). */
export const arGiltigt = (
  bevis: { readonly expires_at: Millis } | null,
  nu: number = Date.now(),
): boolean => bevis !== null && Number(bevis.expires_at) > nu;

/** Appens enda valv. */
export const valvet = new Bevisvalv();
